/**
 * Performance monitoring service
 * Tracks Core Web Vitals and custom metrics
 * Integrates with browser Performance API
 */

export interface PerformanceMetric {
  name: string;
  value: number;
  unit: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

const METRICS: PerformanceMetric[] = [];
const MAX_METRICS = 200;

/**
 * Initialize performance monitoring
 * Captures Core Web Vitals and page load metrics
 */
export function initializePerformanceMonitoring() {
  // Capture page load metrics
  if (window.performance && window.performance.timing) {
    window.addEventListener('load', () => {
      capturePageLoadMetrics();
    });
  }

  // Capture layout shift if supported
  if ('PerformanceObserver' in window) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.entryType === 'navigation') {
            const navEntry = entry as PerformanceNavigationTiming;
            captureMetric('page_load_dns', navEntry.domainLookupEnd - navEntry.domainLookupStart, 'ms');
            captureMetric('page_load_tcp', navEntry.connectEnd - navEntry.connectStart, 'ms');
            captureMetric('page_load_ttfb', navEntry.responseStart - navEntry.requestStart, 'ms');
          }
        }
      });
      observer.observe({ entryTypes: ['navigation'] });
    } catch (e) {
      // Browser doesn't support PerformanceObserver
    }
  }

  // Capture Web Vitals if supported
  captureWebVitals();
}

/**
 * Capture Core Web Vitals (LCP, FID, CLS)
 */
function captureWebVitals() {
  // Largest Contentful Paint (LCP)
  if ('PerformanceObserver' in window) {
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          const lastEntry = entries[entries.length - 1] as PerformanceEntryWithValue;
          captureMetric('web_vital_lcp', lastEntry.renderTime || lastEntry.loadTime || lastEntry.startTime, 'ms');
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      // Browser doesn't support LCP
    }

    // Cumulative Layout Shift (CLS)
    try {
      let cls = 0;
      const clsObserver = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if ((entry as PerformanceEntryWithHadRecentInput).hadRecentInput) continue;
          cls += (entry as PerformanceEntryWithValue).value;
        }
        captureMetric('web_vital_cls', cls, 'score');
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      // Browser doesn't support CLS
    }
  }
}

interface PerformanceEntryWithValue extends PerformanceEntry {
  value: number;
  renderTime?: number;
  loadTime?: number;
}

interface PerformanceEntryWithHadRecentInput extends PerformanceEntry {
  hadRecentInput?: boolean;
}

/**
 * Capture page load metrics
 */
function capturePageLoadMetrics() {
  const perf = window.performance.timing;
  if (!perf) return;

  const navigationStart = perf.navigationStart;
  const metrics = {
    'page_load_interactive': perf.domInteractive - navigationStart,
    'page_load_complete': perf.loadEventEnd - navigationStart,
    'page_load_dom_content': perf.domContentLoadedEventEnd - navigationStart,
    'page_load_resource': perf.loadEventStart - navigationStart
  };

  Object.entries(metrics).forEach(([name, value]) => {
    if (value > 0) captureMetric(name, value, 'ms');
  });
}

/**
 * Capture a custom performance metric
 */
export function captureMetric(
  name: string,
  value: number,
  unit: string = 'ms',
  context?: Record<string, unknown>
) {
  const metric: PerformanceMetric = {
    name,
    value,
    unit,
    timestamp: new Date().toISOString(),
    context
  };

  METRICS.push(metric);
  if (METRICS.length > MAX_METRICS) {
    METRICS.shift(); // Remove oldest
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`[Performance] ${name}: ${value}${unit}`, context);
  }
}

/**
 * Measure and capture a custom operation duration
 */
export async function measureOperation<T>(
  operationName: string,
  operation: () => Promise<T>,
  context?: Record<string, unknown>
): Promise<T> {
  const startTime = performance.now();
  try {
    const result = await operation();
    const duration = performance.now() - startTime;
    captureMetric(`operation_${operationName}`, duration, 'ms', { ...context, success: true });
    return result;
  } catch (err) {
    const duration = performance.now() - startTime;
    captureMetric(`operation_${operationName}_failed`, duration, 'ms', { ...context, success: false });
    throw err;
  }
}

/**
 * Get all captured metrics
 */
export function getAllMetrics(): PerformanceMetric[] {
  return [...METRICS];
}

/**
 * Get metrics by name
 */
export function getMetricsByName(name: string): PerformanceMetric[] {
  return METRICS.filter((m) => m.name === name);
}

/**
 * Calculate metric statistics (avg, min, max)
 */
export function getMetricStats(name: string) {
  const metrics = getMetricsByName(name);
  if (metrics.length === 0) return null;

  const values = metrics.map((m) => m.value);
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);

  return { avg, min, max, count: values.length };
}

/**
 * Clear all metrics
 */
export function clearMetrics() {
  METRICS.length = 0;
}

/**
 * Export metrics for sending to analytics service
 */
export function exportMetrics() {
  return {
    timestamp: new Date().toISOString(),
    pageUrl: window.location.pathname,
    metrics: [...METRICS],
    summary: {
      totalOperations: METRICS.length,
      averagePageLoad: getMetricStats('page_load_complete')
    }
  };
}
